import { createClient } from '@supabase/supabase-js'
import type { Database } from '@/types/database'

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables')
}

// Create Supabase client with type safety
export const supabase = createClient<Database>(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true
  }
})

// Helper functions for common operations
export const supabaseHelpers = {
  // Authentication helpers
  auth: {
    getCurrentUser: () => supabase.auth.getUser(),
    signOut: () => supabase.auth.signOut(),
    signIn: (email: string, password: string) => 
      supabase.auth.signInWithPassword({ email, password }),
    signUp: (email: string, password: string) => 
      supabase.auth.signUp({ email, password }),
    resetPassword: (email: string) =>
      supabase.auth.resetPasswordForEmail(email)
  },

  // File storage helpers
  storage: {
    uploadFile: async (bucket: string, path: string, file: File) => {
      return supabase.storage.from(bucket).upload(path, file, {
        cacheControl: '3600',
        upsert: false
      })
    },
    
    downloadFile: async (bucket: string, path: string) => {
      return supabase.storage.from(bucket).download(path)
    },
    
    getPublicUrl: (bucket: string, path: string) => {
      return supabase.storage.from(bucket).getPublicUrl(path)
    },
    
    deleteFile: async (bucket: string, path: string) => {
      return supabase.storage.from(bucket).remove([path])
    }
  },

  // CAT-specific database operations
  cat: {
    // Projects
    getProjects: (userId: string) =>
      supabase
        .from('cat_projects')
        .select('*')
        .eq('owner_id', userId)
        .order('updated_at', { ascending: false }),

    createProject: (project: Partial<Database['public']['Tables']['cat_projects']['Insert']>) =>
      supabase
        .from('cat_projects')
        .insert(project)
        .select()
        .single(),

    // Segments
    getSegments: (projectId: string) =>
      supabase
        .from('cat_segments')
        .select('*')
        .eq('project_id', projectId)
        .order('segment_order'),

    updateSegment: (segmentId: string, updates: Partial<Database['public']['Tables']['cat_segments']['Update']>) =>
      supabase
        .from('cat_segments')
        .update(updates)
        .eq('id', segmentId)
        .select()
        .single(),

    // Translation Memory
    searchTM: (sourceText: string, sourceLang: string, targetLang: string, userId: string) =>
      supabase
        .from('translation_memory')
        .select('*')
        .eq('source_language', sourceLang)
        .eq('target_language', targetLang)
        .eq('user_id', userId)
        .ilike('source_text', `%${sourceText}%`)
        .order('confidence_score', { ascending: false })
        .limit(10),

    addToTM: (entry: Database['public']['Tables']['translation_memory']['Insert']) =>
      supabase
        .from('translation_memory')
        .insert(entry),

    // Terminology
    searchTerminology: (term: string, sourceLang: string, targetLang: string, userId: string) =>
      supabase
        .from('terminology')
        .select('*')
        .eq('source_language', sourceLang)
        .eq('target_language', targetLang)
        .eq('created_by', userId)
        .or(`term_source.ilike.%${term}%,term_target.ilike.%${term}%`),

    addTerminology: (term: Database['public']['Tables']['terminology']['Insert']) =>
      supabase
        .from('terminology')
        .insert(term)
  }
}

export default supabase
